import { Injectable, inject } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, skip } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Auth } from '../auth/auth';

export interface NotificationChange {
  reason: 'created' | 'read-state' | 'subscribed';
  unreadCount?: number;
}

@Injectable({
  providedIn: 'root',
})
export class BulkUploadSocketService {
  private socket: Socket;
  private messageSubject = new Subject<any>();
  // Every room this session asked to join. The header joins several ids
  // (id / userID / employee_login_id) before the socket has connected, so a
  // single field kept only the last one and the others never got joined —
  // notifications addressed to them were silently dropped.
  private userIds = new Set<string>();
  private notificationToken: string | null = null;
  private readonly auth = inject(Auth);
  private notificationChangeSubject = new Subject<NotificationChange>();

  constructor() {
    this.socket = io(environment.bulkUploadSocketUrl, {
      // WebSocket only: long-polling sends each request separately, and behind a
      // multi-replica host without sticky sessions those requests hit a replica
      // that doesn't know the sid (400 "Session ID unknown"), causing an endless
      // handshake/disconnect loop.
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1000,
    });

    // A group / company switch reissues the access token, and logout clears it. Follow it, so the
    // socket never keeps (or reconnects with) a stale token. The first value is skipped: the tray
    // makes the initial subscription itself, once it has its listeners in place.
    this.auth.user
      .pipe(
        map((user) => user?.accessToken),
        distinctUntilChanged(),
        skip(1),
      )
      .subscribe((token) => {
        if (token) {
          this.subscribeNotifications(token);
        } else {
          this.unsubscribeNotifications();
        }
      });

    this.socket.on('connect', () => {
      console.log('[Socket] Connected — socketId:', this.socket.id);
      this.userIds.forEach((id) => {
        console.log('[Socket] Auto-rejoining room for user:', id);
        this.socket.emit('join', id);
      });
      if (this.notificationToken) {
        this.emitNotificationSubscribe();
      }
    });

    // Signal only (reason + authoritative unread count); content is re-fetched over REST.
    this.socket.on('notifications:changed', (payload: NotificationChange) => {
      this.notificationChangeSubject.next(payload);
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('[Socket] Disconnected — reason:', reason);
    });

    this.socket.on('messageToClient', (msg: any) => {
      console.log('[Socket] messageToClient received:', msg);
      this.messageSubject.next(msg);
    });

    this.socket.on('workerMessage', (msg: any) => {
      console.log('[Socket] workerMessage received:', msg);
      this.messageSubject.next(msg);
    });
  }

  /**
   * Recipient-scoped notification-tray updates. Unlike `joinRoom` (any id), the server only
   * admits this session to its own room after verifying the access token. Re-subscribes on
   * every (re)connect and emits a `subscribed` change so the tray reconciles with the server
   * after an outage instead of trusting whatever pushes it may have missed.
   */
  subscribeNotifications(token: string | undefined | null): void {
    if (!token) return;
    this.notificationToken = token;
    if (this.socket.connected) {
      this.emitNotificationSubscribe();
    }
  }

  unsubscribeNotifications(): void {
    this.notificationToken = null;
    if (this.socket.connected) {
      this.socket.emit('notifications:unsubscribe');
    }
  }

  notificationChanges(): Observable<NotificationChange> {
    return this.notificationChangeSubject.asObservable();
  }

  private emitNotificationSubscribe(): void {
    this.socket.emit('notifications:subscribe', this.notificationToken, (ack: { ok: boolean }) => {
      if (ack?.ok) {
        this.notificationChangeSubject.next({ reason: 'subscribed' });
      } else {
        console.warn('[Socket] Notification subscription refused');
      }
    });
  }

  getMessages(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  joinRoom(userId: string | number): void {
    if (!userId) return;
    const id = String(userId);
    this.userIds.add(id);
    if (this.socket.connected) {
      console.log('[Socket] joinRoom — joining room:', id);
      this.socket.emit('join', id);
    } else {
      console.warn('[Socket] joinRoom called but not connected yet — will auto-join on connect');
    }
  }
}
